import { useRef } from 'react';
import { WidgetContainer } from '@/components/analytics/WidgetContainer';
import { BarChart } from '@/components/analytics/charts/BarChart';
import { useChartDimensions } from '@/hooks/useChartDimensions';
import type { DifficultyBreakdown } from '@/types/analytics';
import styles from './PerformanceByDifficulty.module.scss';

export interface PerformanceByDifficultyProps {
  data: DifficultyBreakdown[] | null;
  isLoading?: boolean;
  error?: Error | null;
}

/**
 * Performance By Difficulty Widget
 *
 * Grouped bar chart showing performance across Easy, Medium, Hard questions.
 */
export function PerformanceByDifficulty({ data, isLoading, error }: PerformanceByDifficultyProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { width } = useChartDimensions(containerRef as any);

  const isEmpty = !data || data.length === 0;

  // Get color for difficulty
  const getDifficultyColor = (difficulty: string): string => {
    switch (difficulty) {
      case 'easy':
        return 'var(--success)';
      case 'medium':
        return 'var(--warning)';
      case 'hard':
        return 'var(--error)';
      default:
        return 'var(--text-tertiary)';
    }
  };

  // Prepare chart data
  const chartData = data
    ? data.map((d) => ({
      label: d.difficulty.charAt(0).toUpperCase() + d.difficulty.slice(1),
      value: d.successRate,
      color: getDifficultyColor(d.difficulty),
    }))
    : [];

  console.log("Chart Data", chartData);
  return (
    <WidgetContainer
      title="Performance by Difficulty"
      description="Success rate across question difficulties"
      isLoading={isLoading}
      error={error}
      isEmpty={isEmpty}
      emptyMessage="Practice questions to see difficulty breakdown"
      size="medium"
    >
      <div ref={containerRef} className={styles.performanceByDifficulty}>
        {/* Chart */}
        {width > 0 && (
          <div className={styles.performanceByDifficulty__chart}>
            <BarChart
              data={chartData}
              width={width}
              height={200}
              showValues
              animate
            />
          </div>
        )}

        {/* Stats */}
        <div className={styles.performanceByDifficulty__stats}>
          {data?.map((d) => (
            <div key={d.difficulty} className={styles.performanceByDifficulty__stat}>
              <span className={styles.performanceByDifficulty__statLabel}>
                {d.difficulty.charAt(0).toUpperCase() + d.difficulty.slice(1)}
              </span>
              <span className={styles.performanceByDifficulty__statValue}>
                {d.correctQuestions}/{d.totalQuestions}
              </span>
            </div>
          ))}
        </div>
      </div>
    </WidgetContainer>
  );
}
